import {
  combineReducers,
  configureStore,
  createListenerMiddleware,
  type UnknownAction,
} from "@reduxjs/toolkit";
import { logoutUser, loginVerifyCode, registerVerifyCode } from "./features/auth/auth.thunk";
import { logout } from "./features/auth/auth.slice";
import { resetAccountMenu } from "./features/accountMenu/accountMenu.slice";
import { getCartCount } from "./features/cart/cart.thunk";
import { sessionStorage } from "./storage/sessionStorage";
import modalReducer from "./features/modal/modal.slice";
import tryItOnReducer from "./features/tryItOn/tryItOn.slice";
import storyReducer from "./features/story/story.slice";
import dialogReducer from "./features/dialog/dialog.slice";
import productDetailReducer from "./features/productDetail/productDetail.slice";
import menusReducer from "./features/menus/menus.slice";
import countriesReducer from "./features/countries/countries.slice";
import authReducer from "./features/auth/auth.slice";
import homeReducer from "./features/home/home.slice";
import agreementsReducer from "./features/agreements/agreements.slice";
import categoriesReducer from "./features/categories/categories.slice";
import customerReducer from "./features/customer/customer.slice";
import alertDialogReducer from "./features/alertDialog/alertDialog.slice";
import cartReducer from "./features/cart/cart.slice";
import checkoutReducer from "./features/checkout/checkout.slice";
import paymentReducer from "./features/payment/payment.slice";
import favoriteReducer from "./features/favorite/favorite.slice";
import locationReducer from "./features/location/location.slice";
import addressReducer from "./features/address/address.slice";
import reorderReducer from "./features/reorder/reorder.slice";
import recentlyViewedReducer from "./features/recently-viewed/recently-viewed.slice";
import followedStoresReducer from "./features/followed-stores/followed-stores.slice";
import ordersReducer from "./features/orders/orders.slice";
import couponReducer from "./features/coupon/coupon.slice";
import settingsReducer from "./features/settings/settings.slice";
import accountMenuReducer from "./features/accountMenu/accountMenu.slice";
import newsletterReducer from "./features/newsletter/newsletter.slice";
import webVisitReducer from "./features/webVisit/webVisit.slice";
import similarProductsReducer from "./features/similarProducts/similarProducts.slice";
import productsReducer from "./features/products/products.slice";
import orderReviewsReducer from "./features/orderReviews/orderReviews.slice";
import reviewsReducer from "./features/reviews/reviews.slice";
import slugControlReducer from "./features/slugControl/slugControl.slice";
import headerReducer from "./features/header/header.slice";
import footerReducer from "./features/footer/footer.slice";
import searchReducer from "./features/search/search.slice";
import productReviewReducer from "./features/productReview/productReview.slice";
import notFoundReducer from "./features/notFound/notFound.slice";
import helpReducer from "./features/help/help.slice";
import collectionReducer from "./features/collection/collection.slice";
import storeReducer from "./features/store/store.slice";
import mobileReducer from "./features/mobile/mobile.slice";
import filtersReducer from "./features/filters/filters.slice";
import genericProductsReducer from "./features/genericProducts/genericProducts.slice";
import genderReducer from "./features/gender/gender.slice";
import shippingTrackingReducer from "./features/shippingTracking/shippingTracking.slice";
import floatingContentReducer from "./features/floatingContent/floatingContent.slice";
import callStartReducer from "./features/callStart/callStart.slice";
import campaignReducer from "./features/campaign/campaign.slice";
import wheelReducer from "./features/wheel/wheel.slice";
import popupReducer from "./features/popup/popup.slice";
import tozyReducer from "./features/tozy/tozy.slice";
import personalizedProductsReducer from "./features/personalizedProducts/personalizedProducts.slice";

const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
  matcher: (action) =>
    logoutUser.fulfilled.match(action) ||
    logoutUser.rejected.match(action) ||
    logout.match(action),
  effect: (_action, listenerApi) => {
    // State temizliği rootReducer'da yapılıyor (aşağıya bkz.). Burada yalnızca
    // yan etkiler kalıyor.
    //
    // 1) session_id yenilenir. Misafir kimliği bu id ile taşınıyor (sepet,
    //    izleme, X-Session-Id header'ı) ve logout'ta hiç dokunulmuyordu: çıkış
    //    yapan kullanıcının oturum kimliği, aynı tarayıcıda devam eden misafire
    //    miras kalıyordu. Login tarafında karşılığı zaten var — mergeCartData
    //    birleştirmeden sonra clearSessionId() çağırıyor.
    // 2) Misafir sepet sayısını hemen çek, yoksa rozet bir sonraki tam sayfa
    //    yüklemesine kadar boş görünür.
    //
    // Sıra önemli: getCartCount isteği X-Session-Id header'ıyla gidiyor, yani
    // yeni id önce yazılmalı — aksi hâlde misafir sepeti eski oturuma bağlanır.
    sessionStorage.refreshSessionId();
    listenerApi.dispatch(getCartCount());
  },
});

listenerMiddleware.startListening({
  matcher: (action) =>
    loginVerifyCode.fulfilled.match(action) || registerVerifyCode.fulfilled.match(action),
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(resetAccountMenu());
  },
});

const appReducer = combineReducers({
    modal: modalReducer,
    countries: countriesReducer,
    auth: authReducer,
    home: homeReducer,
    agreements: agreementsReducer,
    categories: categoriesReducer,
    customer: customerReducer,
    alertDialog: alertDialogReducer,
    cart: cartReducer,
    checkout: checkoutReducer,
    payment: paymentReducer,
    favorite: favoriteReducer,
    location: locationReducer,
    address: addressReducer,
    reorder: reorderReducer,
    recentlyViewed: recentlyViewedReducer,
    followedStores: followedStoresReducer,
    orders: ordersReducer,
    coupon: couponReducer,
    tryItOn: tryItOnReducer,
    story: storyReducer,
    dialog: dialogReducer,
    productDetail: productDetailReducer,
    menus: menusReducer,
    settings: settingsReducer,
    accountMenu: accountMenuReducer,
    newsletter: newsletterReducer,
    webVisit: webVisitReducer,
    similarProducts: similarProductsReducer,
    products: productsReducer,
    orderReviews: orderReviewsReducer,
    reviews: reviewsReducer,
    slugControl: slugControlReducer,
    header: headerReducer,
    footer: footerReducer,
    search: searchReducer,
    productReview: productReviewReducer,
    notFound: notFoundReducer,
    help: helpReducer,
    collection: collectionReducer,
    store: storeReducer,
    mobile: mobileReducer,
    filters: filtersReducer,
    genericProducts: genericProductsReducer,
    gender: genderReducer,
    shippingTracking: shippingTrackingReducer,
    floatingContent: floatingContentReducer,
    callStart: callStartReducer,
    campaign: campaignReducer,
    wheel: wheelReducer,
    popup: popupReducer,
    tozy: tozyReducer,
    personalizedProducts: personalizedProductsReducer,
});

// appReducer'dan türetiliyor (store'dan değil): rootReducer ve PRESERVED_ON_LOGOUT
// bu tipe ihtiyaç duyduğu için store'a bağlamak döngüsel referans yaratırdı.
export type RootState = ReturnType<typeof appReducer>;

/**
 * Logout'ta kullanıcıya özel state'i TEK YERDE sıfırlar.
 *
 * Neden root reducer: logout yalnızca `auth` slice'ını sıfırlıyordu; adresler,
 * siparişler, kuponlar, koleksiyonlar vb. Redux'ta kalmaya devam ediyordu.
 * Sonuç: çıkış yapıp misafir olarak devam eden kullanıcı checkout'ta ÖNCEKİ
 * HESABIN adreslerini görüyordu (refresh atınca store baştan kurulduğu için
 * kayboluyordu — bu yüzden "refresh atınca temizlendi").
 *
 * Bir slice'a `undefined` verildiğinde reducer kendi `initialState`'ini döndürür;
 * bu yüzden slice başına reset action'ı yazmaya/çağırmaya gerek yok.
 *
 * Liste bilinçli olarak KORUNACAKLARI sayar (allow-list): sonradan eklenen her
 * yeni slice varsayılan olarak temizlenir. Ters yönde (temizlenecekleri saymak)
 * bir slice unutulduğunda sessizce veri sızdırırdı.
 */
const PRESERVED_ON_LOGOUT = [
  // Kullanıcıdan bağımsız yapılandırma ve katalog cache'leri — temizlenirse
  // gereksiz yeniden fetch ve skeleton oluşur, kişisel veri içermezler.
  "settings", "menus", "header", "footer", "categories", "countries",
  "location", "help", "mobile", "gender", "slugControl", "notFound",
  "agreements", "newsletter", "webVisit",
  // Katalog/liste ekranları — o an açık olan sayfanın içeriği. Temizlenirse
  // logout anında liste boşalır ve yeniden fetch tetikleyecek bir şey yoktur.
  // Kişiye bağlı tek alan favori durumu; o da `favorite` slice'ından okunuyor
  // ve o slice temizleniyor.
  "productDetail", "products", "genericProducts", "similarProducts", "search",
  "filters", "productReview", "store", "campaign", "story", "floatingContent",
  // UI plumbing — açık modal/dialog'u logout sırasında koparmamak için.
  "modal", "dialog", "alertDialog",
] as const satisfies readonly (keyof RootState)[];

// Bilerek KORUNMAYANLAR (kişiye bağlı olduğu için temizlenenler), gözden
// kaçmasın diye: tozy (AI sohbet geçmişi), personalizedProducts, popup, wheel,
// callStart (canlı destek oturumu), shippingTracking (kargo sorgu sonucu).

const isLogoutAction = (action: UnknownAction) =>
  logout.match(action) ||
  logoutUser.fulfilled.match(action) ||
  logoutUser.rejected.match(action);

const rootReducer = (
  state: RootState | undefined,
  action: UnknownAction,
): RootState => {
  if (state && isLogoutAction(action)) {
    const preserved = {} as Partial<RootState>;
    for (const key of PRESERVED_ON_LOGOUT) {
      preserved[key] = state[key] as never;
    }
    // Kalan anahtarlar undefined kalır → her slice initialState'ine döner.
    state = preserved as RootState;
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: process.env.NODE_ENV === "development"
        ? {
            ignoredPaths: ["modal.modals"],
            ignoredActions: ["modal/append"],
          }
        : false,
      immutableCheck: process.env.NODE_ENV === "development",
    }).prepend(listenerMiddleware.middleware),
  devTools: process.env.NODE_ENV !== "production",
});

export type AppDispatch = typeof store.dispatch;